/**
 * 求人内容設定（/recruit）脱Livewire版のクライアント挙動。
 *
 * Livewire のリアクティブ機能を Alpine.js + 素の JS で置き換える。
 * - 勤務地などの動的行追加/削除（Alpine state）
 * - textarea の自動高さ調整（auto-expand）
 *
 * 画像アップロード(fetch)・選択モーダル・AI生成は後続フェーズで追加する。
 */

// ---------------------------------------------------------------------------
// Alpine ルートコンポーネント（フォーム全体の state）
// ---------------------------------------------------------------------------
document.addEventListener('alpine:init', () => {
    // AI例文の下書き状態（セクション横断で共有）。
    // AI生成で各欄に入った例文は「未確定の下書き」として薄字表示し、
    // 各項目の「編集」→確定で本入力になる。未確定があれば保存をブロックする。
    window.Alpine.store('recruitAi', {
        drafts: {},        // field -> { text, confirmed }
        modalOpen: false,
        editing: null,
        editType: 'textarea',
        editValue: '',
        editOriginal: '',

        setDraft(field, text, original) { this.drafts[field] = { text, confirmed: false, original: original ?? '' }; },
        isUnconfirmed(field) { return !!(this.drafts[field] && !this.drafts[field].confirmed); },
        hasUnconfirmed() { return Object.keys(this.drafts).some((f) => this.drafts[f] && !this.drafts[f].confirmed); },
        openEdit(field, type) {
            this.editing = field;
            this.editType = type || 'textarea';
            this.editOriginal = this.drafts[field] ? this.drafts[field].text : '';
            this.editValue = ''; // 編集エリアは空白で開く（参照欄にAI例文を表示、必要なら「コピー」で取り込む）
            this.modalOpen = true;
        },
        copyOriginal() { this.editValue = this.editOriginal; },
        confirm() {
            const field = this.editing;
            const el = document.getElementById(field);
            if (el) { el.value = this.editValue; el.dispatchEvent(new Event('input', { bubbles: true })); }
            if (this.drafts[field]) this.drafts[field].confirmed = true;
            this.modalOpen = false;
            this.editing = null;
        },
        closeEdit() { this.modalOpen = false; this.editing = null; },
        // すべてリセット：各欄を生成前の値に戻し、下書きをクリア
        resetAll() {
            Object.keys(this.drafts).forEach((f) => {
                const el = document.getElementById(f);
                if (el && this.drafts[f]) { el.value = this.drafts[f].original; el.dispatchEvent(new Event('input', { bubbles: true })); }
            });
            this.drafts = {};
        },
    });

    window.Alpine.data('recruitForm', (initial = {}) => ({
        // 募集要項：勤務地（動的行）
        locations: (initial.locations && initial.locations.length)
            ? initial.locations
            : [{ prefecture_id: 0, address: '' }],

        // 勤務地の上限（Consts::RECRUIT_GUIDELINE_LOCATION_MAX_COUNT = 10）
        locationMax: initial.locationMax || 10,

        saving: false,
        errors: [],
        previewOpen: false,
        // 保存後の完了トースト / 公開必須警告モーダル
        toast: { show: false, message: initial.toastMessage || '' },
        warningOpen: !!initial.hasWarning,
        _toastTimer: null,

        // 未保存変更ガード（旧 discard-confirm-modal 相当を Alpine で再現）
        dirty: false,         // 何か編集したか
        discardOpen: false,   // 破棄確認モーダル表示
        pendingUrl: null,     // 破棄後の遷移先（'back' はブラウザ戻る）
        leaving: false,       // 意図した遷移中（beforeunload を抑止）
        _backTrapArmed: false,// 戻る検知用の履歴トラップを張ったか（未保存変更発生時に1度だけ）

        showToast() {
            this.toast.show = true;
            if (this._toastTimer) clearTimeout(this._toastTimer);
            this._toastTimer = setTimeout(() => { this.toast.show = false; }, 3500);
        },
        closeWarning() {
            this.warningOpen = false;
            // 警告モーダルを閉じたあとに完了トーストを表示
            if (this.toast.message) this.showToast();
        },

        init() {
            // 警告モーダルが無ければ即トースト表示。ある場合はOK後（closeWarning）に表示
            if (this.toast.message && !this.warningOpen) this.showToast();

            // 他コンポーネント（基本条件の即時保存など）からのトースト要求を受け取る
            document.addEventListener('recruit:toast', (e) => {
                if (e.detail && e.detail.message) this.toast.message = e.detail.message;
                this.showToast();
            });

            // Livewire からの通知を受け取る
            if (this.$wire) {
                this.$wire.on('recruit-validation-failed', (data) => {
                    const payload = Array.isArray(data) ? data[0] : data;
                    this.errors = (payload && payload.errors) ? payload.errors : [];
                    this.saving = false;
                    this.leaving = false; // 保存失敗＝ページに留まるので遷移フラグを戻す
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
                this.$wire.on('recruit-preview-ready', (data) => {
                    const payload = Array.isArray(data) ? data[0] : data;
                    this.saving = false;
                    if (payload && payload.url) {
                        window.open(payload.url, '_blank');
                        // プレビューでも全データを実保存している。新規行(仕事内容/先輩)のサーバー採番id が
                        // クライアントに無いまま次に「更新」すると重複作成され、dirty も残って破棄警告が誤発火する。
                        // 保存済みデータで編集ページを再読込し、id と dirty をリフレッシュする。
                        this.leaving = true; // beforeunload 抑止
                        window.location.href = '/recruit-edit';
                    } else {
                        this.leaving = false;
                    }
                });
            }

            // --- 未保存変更ガード ---
            // 戻る検知用の履歴トラップは「未保存変更が出た時」だけ1度だけ積む。
            // 初期に無条件で積むと、変更が無くてもブラウザ戻るが1回余分に消費され
            // 「2回押さないと戻らない」になるため。
            const armBackTrap = () => {
                if (this._backTrapArmed) return;
                this._backTrapArmed = true;
                window.history.pushState({ recruit: true }, '', window.location.href);
            };
            const markDirty = () => { this.dirty = true; armBackTrap(); };
            const form = document.getElementById('recruitForm');
            if (form) {
                form.addEventListener('input', markDirty);
                form.addEventListener('change', markDirty);
            }
            // JS駆動の変更（画像・カード・基本条件など、input が発火しない操作）からも受け取る
            document.addEventListener('recruit:dirty', markDirty);

            window.addEventListener('popstate', () => {
                if (this.dirty && !this.leaving) {
                    // 戻りをキャンセルして確認モーダルを出す（旧実装と同等）
                    window.history.pushState({ recruit: true }, '', window.location.href);
                    this.pendingUrl = 'back';
                    this.discardOpen = true;
                }
            });
            // タブ閉じ／リロード／外部リンク等のネイティブ離脱
            window.addEventListener('beforeunload', (e) => {
                if (this.dirty && !this.leaving) { e.preventDefault(); e.returnValue = ''; }
            });

            // 代理タブが無効化される瞬間の未保存退避（Issue #625 拡張 / 仕様①②）。
            //  ① 別タブで別企業にログイン（company-context-stale）
            //  ② 本顧客ログインによる追い出し（company-presence-kicked）
            // どちらも「編集中(dirty)のときだけ」未保存フォームを form_draft として退避する。
            // 本番テーブルには書かない。企業IDはタブ保持値(window.__COMPANY_CTX)を使い、
            // 専用エンドポイント(company.context 除外)へ送る（①はセッションが別企業に上書き済みのため）。
            window.addEventListener('company-presence-kicked', () => this.stashDraftOnKick());
            window.addEventListener('company-context-stale', () => this.stashDraftOnKick());
        },

        // 追い出し時の未保存ドラフト退避（変更があった時のみ）。UI ブロックは別部品が担当。
        stashDraftOnKick() {
            if (!this.dirty || this._draftStashed) return;
            this._draftStashed = true; // 二重送信防止
            try {
                const form = document.getElementById('recruitForm');
                const payload = this.serializeForm(form);
                this.sendKickDraft(payload);
            } catch (e) { /* 退避失敗はUIを妨げない */ }
        },

        sendKickDraft(payload) {
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';
            const url = '/recruit/stash-draft';
            // 企業IDはタブ保持値を明示送信する（①はセッションが別企業に上書き済みで頼れないため）。
            const companyId = String(window.__COMPANY_CTX?.companyId || '');
            if (!companyId) return;
            const json = JSON.stringify({ companyId: companyId, payload });

            if (navigator.sendBeacon) {
                const data = new FormData();
                data.append('_token', csrf);
                data.append('companyId', companyId);
                data.append('payload', JSON.stringify(payload));
                if (navigator.sendBeacon(url, data)) return;
            }

            // X-Company-Ctx は付けない：万一 company.context ミドルウェアが効いていても
            // 「ヘッダ無し＝フェイルオープン」で通す。企業IDは body の companyId で渡す。
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrf,
                },
                body: json,
                keepalive: true,
            }).catch(() => {});
        },

        // 「もどる」等の離脱要求。未保存があれば破棄確認、無ければ即遷移
        requestLeave(url) {
            if (this.dirty) {
                this.pendingUrl = url;
                this.discardOpen = true;
            } else {
                this.leaving = true;
                window.location.href = url;
            }
        },
        confirmDiscard() {
            this.leaving = true;
            this.dirty = false;
            this.discardOpen = false;
            // 離脱する前にフォームをサーバー描画時の値（=保存済み値）へ戻す。
            // これで bfcache に凍結されるスナップショットがクリーンになり、戻る/進むで
            // 本ページに戻っても「破棄したはずの未保存値」が残らない（再読込・フラッシュ不要）。
            const form = document.getElementById('recruitForm');
            if (form) form.reset();
            const url = this.pendingUrl;
            this.pendingUrl = null;
            if (url === 'back') {
                window.history.go(-2); // armBackTrap と popstate で積んだ2エントリ分戻す
            } else if (url) {
                window.location.href = url;
            }
        },
        cancelDiscard() {
            this.discardOpen = false;
            this.pendingUrl = null;
        },

        addLocation() {
            if (this.locations.length >= this.locationMax) return;
            this.locations.push({ prefecture_id: 0, address: '' });
            this.dirty = true;
        },

        removeLocation(index) {
            if (this.locations.length <= 1) return;
            this.locations.splice(index, 1);
            this.dirty = true;
        },

        // <form> を直列化して入れ子オブジェクトにし、保存だけ1往復で送る
        save(forPreview = false) {
            if (this.saving) return;
            // AI例文の未確定下書きがあれば保存をブロック（元画面と同挙動）
            const ai = window.Alpine && window.Alpine.store('recruitAi');
            if (ai && ai.hasUnconfirmed()) {
                const first = document.querySelector('.ai-draft-review-container');
                if (first) {
                    const y = first.getBoundingClientRect().top + window.pageYOffset - 80;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
                alert('AI生成で例文になっている箇所があります。内容を確認・確定してください。');
                return;
            }
            this.errors = [];
            this.saving = true;

            const form = document.getElementById('recruitForm');
            const payload = this.serializeForm(form);
            if (forPreview) payload.for_preview = 1;

            // 保存成功時はサーバーが redirect する。その離脱で未保存警告が出ないよう抑止
            this.leaving = true;

            // 失敗時は recruit-validation-failed、成功時は redirect / preview イベントで処理
            Promise.resolve(this.$wire.save(payload)).catch(() => {
                this.saving = false;
                this.leaving = false;
                this.errors = ['保存中にエラーが発生しました。時間をおいて再度お試しください。'];
            });
        },

        serializeForm(form) {
            const obj = {};
            if (!form) return obj;
            const fd = new FormData(form);
            for (const [key, value] of fd.entries()) {
                if (key === '_token') continue; // Livewire 側で CSRF 済み
                this.assignNested(obj, key, value);
            }
            return obj;
        },

        // PHP風の name="a[b][0][c]" を入れ子オブジェクトへ
        assignNested(obj, key, value) {
            const path = key.replace(/\]/g, '').split('[');
            let cur = obj;
            for (let i = 0; i < path.length; i++) {
                const k = path[i];
                if (i === path.length - 1) {
                    cur[k] = value;
                } else {
                    if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
                    cur = cur[k];
                }
            }
        },
    }));
});

// ---------------------------------------------------------------------------
// フォーム全体を PHP風 name から入れ子オブジェクトへ直列化（モジュール公開）。
// 基本条件の即時保存（recruit-basic.js → /recruit/basic）から再利用する。
// ---------------------------------------------------------------------------
window.recruitSerializeForm = function () {
    const obj = {};
    const form = document.getElementById('recruitForm');
    if (!form) return obj;
    const fd = new FormData(form);
    for (const [key, value] of fd.entries()) {
        if (key === '_token') continue;
        const path = key.replace(/\]/g, '').split('[');
        let cur = obj;
        for (let i = 0; i < path.length; i++) {
            const k = path[i];
            if (i === path.length - 1) cur[k] = value;
            else { if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {}; cur = cur[k]; }
        }
    }
    return obj;
};

// ---------------------------------------------------------------------------
// textarea 自動高さ調整（旧 crecruit.blade.php のロジックを移植）
// ---------------------------------------------------------------------------
(function () {
    function autoExpand(el) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }

    function initAutoExpand(root) {
        (root || document).querySelectorAll('.auto-expand').forEach((ta) => {
            autoExpand(ta);
            if (!ta.dataset.autoExpandBound) {
                ta.dataset.autoExpandBound = '1';
                ta.addEventListener('input', function () { autoExpand(this); });
            }
        });
    }

    document.addEventListener('DOMContentLoaded', () => {
        const page = document.querySelector('.page-crecruit');
        if (!page) return;

        initAutoExpand(page);

        // Alpine の x-for などで動的追加された textarea にも対応
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.addedNodes.length > 0) {
                    requestAnimationFrame(() => initAutoExpand(page));
                    break;
                }
            }
        });
        observer.observe(page, { childList: true, subtree: true });

        // ページ最下部の更新ボタンスクロール等の共通挙動はここに追加予定
    });

    // 他モジュールから呼べるよう公開
    window.recruitAutoExpand = initAutoExpand;
})();

// ---------------------------------------------------------------------------
// 画像アップロード（Dropify維持・Livewire非依存版）
//
// 旧 crecruit-*.blade.php の Dropify + jQuery AJAX を踏襲しつつ、
// 状態保持を Livewire dispatch から hidden input(.js-img-media-id) へ置換する。
//
// マークアップ契約（各セクションは既存の見た目クラスに加え下記フックを付与）:
//   .recruit-image-group[data-ecid][data-img-fixed]   … グループ
//     .recruit-image-slot[data-index]                  … 1スロット
//       input.js-img-media-id  (hidden, name="...[i][image_media_id]")
//       .js-dropify-area        … Dropify を挿す箱（見た目クラスと併用）
//       .js-img-remove          … ×削除ボタン
//     .js-img-add               … 「＋画像添付」ボタン（可変グループのみ）
// ---------------------------------------------------------------------------
(function () {
    // Dropify 枠の1枚あたりの上限（data-max-file-size）。サーバー側でも同じ上限と形式を確かめる
    const MAX_FILE_SIZE = '4M';
    // Dropify のエラー表示を消すまでの時間。既定の3秒では見落とすため、setTimeout の上限（約24日）
    // ＝実質「次に画像を選び直すか削除するまで」残す
    const KEEP_ERROR_MS = 2147483647;
    const UPLOAD_FAILED_MESSAGE = '画像のアップロードに失敗しました。時間をおいて、もう一度お試しください。';

    // /imageUpload の失敗を、利用者が理由の分かる文にする（Dropify 枠・掲載見出しのクロップ画像で共通）。
    // null は表示しない（409 は別企業への切替で、企業切替ガードがモーダルで知らせる）。
    function uploadErrorMessage(xhr, textStatus) {
        if (textStatus === 'timeout') {
            return '通信に時間がかかりすぎたため、画像を送信できませんでした。通信環境の良い場所で、もう一度お試しください。';
        }
        switch (xhr.status) {
            case 0:
                return '通信エラーのため、画像を送信できませんでした。インターネットの接続を確認して、もう一度お試しください。';
            case 409:
                return null;
            case 413:
                return '画像のサイズが大きすぎるため、送信できませんでした。サイズの小さい画像を選んでください。';
            case 419:
                return 'ページの有効期限が切れたため、画像を送信できませんでした。ページを再読み込みしてから、もう一度お試しください。';
            case 422:
                // 形式が対応していない等。理由はサーバー（CompanyImageUpload）が message で返す
                return (xhr.responseJSON && xhr.responseJSON.message) || UPLOAD_FAILED_MESSAGE;
            default:
                return UPLOAD_FAILED_MESSAGE;
        }
    }
    function alertUploadError(xhr, textStatus) {
        const message = uploadErrorMessage(xhr, textStatus);
        if (message) alert(message);
    }

    function init(root) {
        const $ = window.jQuery;
        if (!$) return;
        const csrf = $('meta[name="csrf-token"]').attr('content');

        $(root || document).find('.recruit-image-group').each(function () {
            const $group = $(this);
            if ($group.data('img-crop')) return;   // クロップ対応グループ(header)は別系統で処理
            if ($group.data('rigBound')) return;
            $group.data('rigBound', true);

            const ecid = $group.data('ecid');
            const fixed = !!$group.data('img-fixed');

            // 「＋画像添付」ボタン（outline等）は、未表示スロットが残っている間だけ表示。
            // ボタンはグループ外（.recruit-image-section 内・グリッド下）に置かれる場合がある。
            function updateAddButton() {
                let $add = $group.find('.js-img-add');
                if (!$add.length) $add = $group.closest('.recruit-image-section').find('.js-img-add');
                if (!$add.length) return; // selection/point は追加ボタン無し
                const hasHidden = $group.find('.recruit-image-slot').filter(':hidden').length > 0;
                $add.toggle(hasHidden);
            }

            function initSlot($slot) {
                const $area = $slot.find('.js-dropify-area').first();
                if (!$area.length || $area.data('dropifyInitialized')) return;
                const $hidden = $slot.find('.js-img-media-id').first();

                const $file = $('<input type="file" class="dropify" data-max-file-size="' + MAX_FILE_SIZE + '" accept=".jpg,.jpeg,.png,.gif" />');
                $area.append($file);

                const mid = $hidden.val();
                if (mid) {
                    $file.attr('data-default-file', '/storage/company_media/' + mid + '.jpg');
                }

                $file.data('allowed-file-extensions', 'jpg jpeg gif png');
                $file.dropify({
                    // Dropify はプレビューする大きさ（maxFileSizePreview 未満）の画像だけ、判定結果の反映が
                    // 読み込み後（非同期）になる。既定の5MBのままだと 4〜5MB は弾く前に下の change で送信が
                    // 走り、保存されたうえでエラーも出ていた。上限にそろえ、超えたものは選んだ時点で入力ごと
                    // 消させる（＝送らない）
                    maxFileSizePreview: MAX_FILE_SIZE,
                    errorTimeout: KEEP_ERROR_MS,
                    messages: {
                        default: 'クリック<br>またはドラッグ＆ドロップ',
                        replace: 'クリック<br>またはドラッグ＆ドロップ',
                        remove: '削除',
                        error: 'エラーが発生しました',
                    },
                    error: {
                        fileSize: 'ファイルサイズが大きすぎます。4MB以内の画像を選んでください。',
                        fileExtension: 'この形式のファイルは使えません。jpg / png / gif の画像を選んでください。',
                    },
                });
                $area.find('.dropify-infos').remove();

                // 枠を hidden の画像ID（保存済みの画像。無ければ空）から作り直す。
                // 送信に成功したときは新しい画像を既定にするため、失敗したときは選んだ画像のプレビューが
                // 残って「選べたように見える」のを防ぐため（hidden は失敗では変わらないので選ぶ前の状態に戻る）
                function rebuildSlot() {
                    const inst = $file.data('dropify');
                    if (inst) inst.destroy();
                    $area.empty().removeData('dropifyInitialized');
                    initSlot($slot);
                }

                $file.on('change', function () {
                    // Dropify が弾いたファイル（4MB超、.webp・.heic など）は入力が空になっている。エラーは Dropify が表示済み
                    if (!this.files || !this.files[0]) return;
                    // 受け付けたので、残していた前回のエラー表示を消す
                    $area.find('.dropify-errors-container').removeClass('visible');
                    const fd = new FormData();
                    fd.append('file', this.files[0]);
                    fd.append('eCid', ecid);
                    $.ajax({
                        type: 'POST', url: '/imageUpload', data: fd,
                        processData: false, contentType: false, dataType: 'json',
                        timeout: 15000, cache: false,
                        headers: { 'X-CSRF-TOKEN': csrf },
                        success: function (data) {
                            if (data && data.mediaId) {
                                $hidden.val(data.mediaId);
                                $slot.find('.js-img-remove').show();
                                // アップロードした画像を Dropify の既定(data-default-file)として再初期化する。
                                // これをしないと「再クリック→キャンセル」で Dropify が既定(=空)へ戻り、
                                // プレビューが消える（hidden値は残るため保存自体は可能）。
                                rebuildSlot();
                                document.dispatchEvent(new Event('recruit:dirty'));
                            } else {
                                rebuildSlot();
                                alert(UPLOAD_FAILED_MESSAGE);
                            }
                        },
                        error: function (xhr, textStatus) {
                            rebuildSlot();
                            alertUploadError(xhr, textStatus);
                        },
                    });
                });

                // Dropify内蔵の削除リンク
                $area.on('click', '.dropify-clear', function () { $hidden.val(''); document.dispatchEvent(new Event('recruit:dirty')); });

                $area.data('dropifyInitialized', true);
            }

            // 表示済みスロットを初期化
            $group.find('.recruit-image-slot').each(function () {
                if ($(this).is(':visible')) initSlot($(this));
            });
            updateAddButton();

            // 可変グループ(outline等)のスロットを DOM 順に index を振り直す。
            // 削除スロットを末尾へ移動した後に呼び、name の添字 = 表示順 = 保存順に揃える。
            function renumber() {
                const baseName = $group.find('.js-img-media-id').first().attr('name') || '';
                const prefix = baseName.split('[')[0];
                if (!prefix) return;
                $group.find('.recruit-image-slot').each(function (i) {
                    const $s = $(this);
                    $s.attr('data-index', i).data('index', i);
                    $s.find('.js-img-media-id').attr('name', `${prefix}[${i}][image_media_id]`);
                    $s.find('input[type="text"]').attr('name', `${prefix}[${i}][caption]`);
                });
            }

            // ×削除
            $group.on('click', '.js-img-remove', function (e) {
                e.preventDefault();
                const $slot = $(this).closest('.recruit-image-slot');
                $slot.find('.js-img-media-id').first().val('');
                $slot.find('input[type="text"]').val(''); // ① 説明文も一緒に削除（残骸の復活防止）
                const $area = $slot.find('.js-dropify-area').first();
                const $input = $area.find('input.dropify');
                const inst = $input.data('dropify');
                if (inst) inst.destroy();
                $area.empty().removeData('dropifyInitialized');
                const idx = parseInt($slot.data('index'), 10);
                if (!fixed && idx !== 0) {
                    $slot.hide();
                    $group.append($slot); // ② 空きスロットを末尾へ移動 → 再追加は割り込まず最後に入る
                    renumber();           //    DOM順で添字を振り直し（表示順＝保存順を維持）
                } else {
                    initSlot($slot);
                }
                $(this).hide();
                updateAddButton(); // 空きスロットが復活したら追加ボタンを戻す（outline用）
                document.dispatchEvent(new Event('recruit:dirty'));
            });

            // 外部（グリッド下）の追加ボタンからも操作できるよう公開
            $group.data('rigInitSlot', initSlot);
            $group.data('rigUpdateAdd', updateAddButton);
        });
    }

    // ＋画像添付：ボタンは .recruit-image-group の外（グリッド下）でも可。
    // 次の非表示スロットを開き、その枠に削除(×)ボタンを表示する（元画面と同挙動）。
    function bindAddButton() {
        const $ = window.jQuery;
        if (!$) return;
        $(document).off('click.rigAdd').on('click.rigAdd', '.js-img-add', function (e) {
            e.preventDefault();
            let $group = $(this).closest('.recruit-image-section').find('.recruit-image-group').first();
            if (!$group.length) $group = $(this).closest('.recruit-image-group');
            if (!$group.length) return;
            const initSlot = $group.data('rigInitSlot');
            const updateAddButton = $group.data('rigUpdateAdd');
            const $next = $group.find('.recruit-image-slot').filter(':hidden').first();
            if ($next.length) {
                $next.show();
                $next.find('.js-img-remove').show(); // 追加枠に削除(×)ボタンを出す
                if (initSlot) initSlot($next);
            }
            if (updateAddButton) updateAddButton();
        });
    }

    // -----------------------------------------------------------------------
    // クロップ対応画像グループ（掲載見出し）。Dropify ではなくクロッパー経由で
    // アップロードする（data-img-crop="アスペクト比"）。
    //   .recruit-image-slot
    //     input.js-img-media-id (hidden)
    //     .js-crop-trigger > img.js-crop-preview + .js-crop-placeholder
    //     input.js-crop-input (file, hidden)
    //     .js-img-remove
    // -----------------------------------------------------------------------
    function initCropGroups(root) {
        const $ = window.jQuery;
        if (!$) return;
        const csrf = $('meta[name="csrf-token"]').attr('content');

        $(root || document).find('.recruit-image-group[data-img-crop]').each(function () {
            const $group = $(this);
            if ($group.data('cropBound')) return;
            $group.data('cropBound', true);
            const ecid = $group.data('ecid');
            const aspect = parseFloat($group.data('img-crop')) || (16 / 9);

            $group.find('.recruit-image-slot').each(function () {
                const $slot = $(this);
                const $hidden = $slot.find('.js-img-media-id').first();
                const $input = $slot.find('.js-crop-input').first();
                const $frame = $slot.find('.image-frame').first();
                const $preview = $slot.find('.preview-img').first();
                const $empty = $slot.find('.empty-state').first();
                const $remove = $slot.find('.delete-button').first();

                function showPreview(url) {
                    $preview.attr('src', url).show();
                    $empty.hide();
                    $remove.show();
                    $frame.addClass('has-image');
                }
                function clearPreview() {
                    $preview.attr('src', '').hide();
                    $empty.show();
                    $remove.hide();
                    $frame.removeClass('has-image');
                }

                function upload(file) {
                    const fd = new FormData();
                    fd.append('file', file);
                    fd.append('eCid', ecid);
                    $.ajax({
                        type: 'POST', url: '/imageUpload', data: fd,
                        processData: false, contentType: false, dataType: 'json',
                        timeout: 15000, cache: false, headers: { 'X-CSRF-TOKEN': csrf },
                        success: (data) => {
                            if (data && data.mediaId) { $hidden.val(data.mediaId); showPreview('/storage/company_media/' + data.mediaId + '.jpg'); document.dispatchEvent(new Event('recruit:dirty')); }
                            else alert(UPLOAD_FAILED_MESSAGE);
                        },
                        error: alertUploadError,
                    });
                }
                function handleFile(file) {
                    if (!file) return;
                    if (window.imageCropper) {
                        window.imageCropper.open(file, (cropped) => upload(cropped), aspect);
                    } else {
                        upload(file);
                    }
                }

                // フレームクリックでファイル選択（削除ボタンクリックは除外）
                $frame.on('click', (e) => {
                    if ($(e.target).closest('.delete-button').length) return;
                    $input.trigger('click');
                });
                // ドラッグ&ドロップ
                $frame.on('dragover', (e) => { e.preventDefault(); $frame.addClass('drag-over'); });
                $frame.on('dragleave', (e) => { e.preventDefault(); $frame.removeClass('drag-over'); });
                $frame.on('drop', (e) => {
                    e.preventDefault();
                    $frame.removeClass('drag-over');
                    const f = e.originalEvent.dataTransfer.files && e.originalEvent.dataTransfer.files[0];
                    handleFile(f);
                });
                $input.on('change', function () { handleFile(this.files && this.files[0]); this.value = ''; });
                $remove.on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    $hidden.val('');
                    $slot.find('input[type="text"]').val(''); // 説明文も一緒に削除
                    clearPreview();
                    document.dispatchEvent(new Event('recruit:dirty'));
                });
            });
        });
    }

    document.addEventListener('DOMContentLoaded', () => { init(document); bindAddButton(); initCropGroups(document); });
    window.initRecruitImageGroups = (root) => { init(root); initCropGroups(root); };
})();

// ---------------------------------------------------------------------------
// カードリピーター（仕事内容 / 先輩から一言）
//
// Dropify(jQuery) と Alpine x-for は相性が悪いため、カードの追加/削除は
// <template> クローン方式の素 JS で行う（locations 等の純 Alpine とは別系統）。
//  - 追加: data-template の中身を複製し __I__ を採番indexへ置換 → 画像Dropify初期化
//  - 削除: 既存(id有り)は is_deleted=1 にして hide（保存時に削除）、新規はDOM除去
//  - 採番と「記事を削除」表示(2枚以上)を再計算
// ---------------------------------------------------------------------------
(function () {
    function renumber($container) {
        const $ = window.jQuery;
        const $active = $container.find('.js-card').filter(function () {
            return $(this).find('.js-is-deleted').val() !== '1' && $(this).is(':visible');
        });
        $active.each(function (i) { $(this).find('.js-card-no').text(i + 1); });
        $container.find('.js-card').each(function () {
            $(this).find('.js-remove-card').toggle($active.length > 1);
        });
    }

    function bind() {
        const $ = window.jQuery;
        if (!$) return;

        $(document).off('click.recruitAddCard').on('click.recruitAddCard', '.js-add-card', function (e) {
            e.preventDefault();
            const $btn = $(this);
            const tpl = document.getElementById($btn.data('template'));
            const $container = $btn.closest('.section').find($btn.data('container'));
            if (!tpl || !$container.length) return;

            let counter = parseInt($container.attr('data-counter') || '0', 10);
            const idx = counter;
            $container.attr('data-counter', counter + 1);

            const html = tpl.innerHTML.replace(/__I__/g, idx);
            const $card = $(html);
            $container.append($card);

            if (window.initRecruitImageGroups) window.initRecruitImageGroups($card[0]);
            if (window.recruitAutoExpand) window.recruitAutoExpand($card[0]);
            renumber($container);
            document.dispatchEvent(new Event('recruit:dirty'));
        });

        $(document).off('click.recruitRemoveCard').on('click.recruitRemoveCard', '.js-remove-card', function (e) {
            e.preventDefault();
            const $card = $(this).closest('.js-card');
            const $container = $card.closest('.js-cards');
            const $id = $card.find('.js-card-id');
            if ($id.length && $id.val()) {
                $card.find('.js-is-deleted').val('1');
                $card.hide();
            } else {
                $card.remove();
            }
            renumber($container);
            document.dispatchEvent(new Event('recruit:dirty'));
        });
    }

    document.addEventListener('DOMContentLoaded', () => {
        bind();
        const $ = window.jQuery;
        if ($) $('.js-cards').each(function () { renumber($(this)); });
    });
})();
